import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Button, 
    Divider, 
    Alert,
    CircularProgress,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Checkbox,
    ListItemText,
    IconButton,
    Dialog,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Icon } from '../../icons';
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from '../../constants/color';
import { useAuth } from '../../context/authContext';
import { url } from '../../constants/url';
import axios from 'axios';
import { useFlowToggle } from '../../context/flowToggleContext';

const customMenuProps = {
    PaperProps: {
        sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
            border: `1px solid ${COLORS.BORDER || '#eee'}`,
            mt: 1,
            maxHeight: 250,
            '& .MuiMenuItem-root': {
                fontFamily: FONT_FAMILY,
                fontSize: '14px',
                borderRadius: '8px',
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                    backgroundColor: `${COLORS.PRIMARY}12`,
                    color: COLORS.PRIMARY,
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: `${COLORS.PRIMARY}20`,
                    }
                },
                '&:hover': {
                    backgroundColor: '#f5f5f5',
                }
            }
        }
    }
};

const ExportData = () => {
    const { token } = useAuth();
    const { isTechnicalFlow } = useFlowToggle();
    
    // Filter states
    const [projectFilters, setProjectFilters] = useState({
        fromDate: '',
        toDate: '',
        status: 'all',
        propertyType: 'all'
    });
    const [userFilters, setUserFilters] = useState({
        fromDate: '',
        toDate: '',
        role: 'all',
        status: 'all'
    });

    const [loading, setLoading] = useState({ projects: false, users: false });
    const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

    // Custom check export/import states
    const [issueCategories, setIssueCategories] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState(['all']);
    const [selectedStatus, setSelectedStatus] = useState('PENDING');
    const [selectedFile, setSelectedFile] = useState(null);
    const [loadingCustom, setLoadingCustom] = useState(false);
    const [loadingImport, setLoadingImport] = useState(false);
    const [popupMsg, setPopupMsg] = useState({ open: false, type: 'success', title: '', text: '' });

    const handleCategoryChange = (event) => {
        const {
            target: { value },
        } = event;
        if (value.includes('all')) {
            if (selectedCategoryIds.includes('all')) {
                // "all" was already selected, so the user clicked a specific category. Remove "all"
                const filtered = value.filter(val => val !== 'all');
                setSelectedCategoryIds(filtered.length === 0 ? ['all'] : filtered);
            } else {
                // "all" was selected, so clear other choices
                setSelectedCategoryIds(['all']);
            }
        } else {
            setSelectedCategoryIds(value.length === 0 ? ['all'] : value);
        }
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get(`${url}/api/issue-categories`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data && res.data.success) {
                    const list = Array.isArray(res.data.data) ? res.data.data : [];
                    setIssueCategories(list);
                } else if (Array.isArray(res.data)) {
                    setIssueCategories(res.data);
                }
            } catch (err) {
                console.error("Error fetching issue categories:", err);
            }
        };
        if (token) {
            fetchCategories();
        }
    }, [token]);

    const handleExport = async (type, subType = '') => {
        try {
            const loadingKey = subType || type;
            setLoading(prev => ({ ...prev, [loadingKey]: true }));
            setStatusMsg({ type: '', text: '' });

            const endpoint = type === 'projects' ? '/api/projects/export/csv' : '/api/users/export/csv';
            const filters = type === 'projects' ? projectFilters : userFilters;

            const params = {
                ...filters,
                portal: isTechnicalFlow ? 'technical' : 'main'
            };
            if (subType) {
                params.subType = subType;
            }

            const response = await axios.get(`${url}${endpoint}`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create a link and trigger download
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            setPopupMsg({
                open: true,
                type: 'success',
                title: 'Export Successful',
                text: `${type.charAt(0).toUpperCase() + type.slice(1)} data has been downloaded as CSV successfully.`
            });
        } catch (err) {
            console.error(`Error exporting ${type}:`, err);
            setPopupMsg({
                open: true,
                type: 'error',
                title: 'Export Failed',
                text: `Failed to export ${type}. Please try again.`
            });
        } finally {
            const loadingKey = subType || type;
            setLoading(prev => ({ ...prev, [loadingKey]: false }));
        }
    };

    const handleExportCustomChecks = async () => {
        try {
            setLoadingCustom(true);
            setStatusMsg({ type: '', text: '' });

            const categoryParam = selectedCategoryIds.join(',');
            const response = await axios.get(`${url}/api/custom-checks/${categoryParam}/export/csv`, {
                params: { status: selectedStatus },
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', `custom_checks_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            setPopupMsg({
                open: true,
                type: 'success',
                title: 'Export Successful',
                text: "Custom questions have been downloaded as CSV successfully."
            });
        } catch (err) {
            console.error("Error exporting custom checks:", err);
            setPopupMsg({
                open: true,
                type: 'error',
                title: 'Export Failed',
                text: "Failed to export custom questions. Please check permissions and try again."
            });
        } finally {
            setLoadingCustom(false);
        }
    };

    const handleImportExcel = async () => {
        if (!selectedFile) {
            setPopupMsg({
                open: true,
                type: 'error',
                title: 'Selection Required',
                text: "Please select an Excel or CSV file to import."
            });
            return;
        }

        try {
            setLoadingImport(true);
            setStatusMsg({ type: '', text: '' });

            const formData = new FormData();
            formData.append('file', selectedFile);

            const res = await axios.post(`${url}/api/issue-categories/checks/import-excel`, formData, {
                headers: { 
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res.data && res.data.success) {
                setPopupMsg({
                    open: true,
                    type: 'success',
                    title: 'Import Successful',
                    text: res.data.message || "Excel sheet imported successfully!"
                });
                setSelectedFile(null);
                const fileInput = document.getElementById('excel-file-input');
                if (fileInput) fileInput.value = '';
            } else {
                setPopupMsg({
                    open: true,
                    type: 'error',
                    title: 'Import Failed',
                    text: "Failed to import predefined checks."
                });
            }
        } catch (err) {
            console.error("Error importing Excel:", err);
            const errMsg = err.response?.data?.message || "Failed to import Excel sheet. Ensure headers match the template format.";
            setPopupMsg({
                open: true,
                type: 'error',
                title: 'Import Failed',
                text: errMsg
            });
        } finally {
            setLoadingImport(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, width: '100%', maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Box sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '12px', 
                    backgroundColor: `${COLORS.PRIMARY}15`, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                }}>
                    <Icon name="download" sx={{ fontSize: 24, color: COLORS.PRIMARY }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_FAMILY,
                            ...TYPOGRAPHY.H2,
                            color: COLORS.TEXT,
                            fontWeight: 800
                        }}
                    >
                        Export Data - {isTechnicalFlow ? 'Field Inspection Portal' : 'Main Portal'}
                    </Typography>
                    <Typography sx={{ color: COLORS.LIGHT_SECONDARY, fontSize: '14px' }}>
                        Download filtered {isTechnicalFlow ? 'field inspection' : 'project'} and user records in CSV format
                    </Typography>
                </Box>
            </Box>

            {statusMsg.text && (
                <Alert severity={statusMsg.type} sx={{ mb: 3, borderRadius: '12px' }}>
                    {statusMsg.text}
                </Alert>
            )}
            
            <Grid container spacing={4} sx={{ width: '100%' }}>
                {/* Project Export */}
                <Grid item xs={12} sx={{ width: '100%' }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            borderRadius: '20px', 
                            border: `1px solid ${COLORS.BORDER || '#eee'}`,
                            background: '#fff',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Icon name="projects" size={24} color={COLORS.PRIMARY} />
                            <Typography sx={{ fontWeight: 800, color: COLORS.TEXT, fontSize: '20px' }}>
                                {isTechnicalFlow ? 'Field Inspection Records' : 'Project Records'}
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="From Date"
                                    type="date"
                                    fullWidth
                                    value={projectFilters.fromDate}
                                    onChange={(e) => setProjectFilters({ ...projectFilters, fromDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={{ '& input': { textTransform: 'uppercase' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="To Date"
                                    type="date"
                                    fullWidth
                                    value={projectFilters.toDate}
                                    onChange={(e) => setProjectFilters({ ...projectFilters, toDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={{ '& input': { textTransform: 'uppercase' } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth size="small">
                                    {isTechnicalFlow ? (
                                        <>
                                            <InputLabel>Property Type</InputLabel>
                                            <Select
                                                value={projectFilters.propertyType || 'all'}
                                                label="Property Type"
                                                onChange={(e) => setProjectFilters({ ...projectFilters, propertyType: e.target.value })}
                                            >
                                                <MenuItem value="all">All Property Types</MenuItem>
                                                <MenuItem value="1 BHK">1 BHK</MenuItem>
                                                <MenuItem value="2 BHK">2 BHK</MenuItem>
                                                <MenuItem value="2.5 BHK">2.5 BHK</MenuItem>
                                                <MenuItem value="3 BHK">3 BHK</MenuItem>
                                                <MenuItem value="4 BHK">4 BHK</MenuItem>
                                                <MenuItem value="5 BHK">5 BHK</MenuItem>
                                                <MenuItem value="Studio">Studio</MenuItem>
                                                <MenuItem value="Bungalow">Bungalow</MenuItem>
                                                <MenuItem value="Shop / Office (Up to 1000 sq ft)">Shop / Office (Up to 1000 sq ft)</MenuItem>
                                                <MenuItem value="Shop / Office (1000-5000 sq ft)">Shop / Office (1000-5000 sq ft)</MenuItem>
                                            </Select>
                                        </>
                                    ) : (
                                        <>
                                            <InputLabel>Project Status</InputLabel>
                                            <Select
                                                value={projectFilters.status}
                                                label="Project Status"
                                                onChange={(e) => setProjectFilters({ ...projectFilters, status: e.target.value })}
                                            >
                                                <MenuItem value="all">All Statuses</MenuItem>
                                                <MenuItem value="UNASSIGNED">Unassigned</MenuItem>
                                                <MenuItem value="ASSIGNED">Assigned</MenuItem>
                                                <MenuItem value="VISITED">Visited</MenuItem>
                                                <MenuItem value="SIGNPENDING">Signature Pending</MenuItem>
                                                <MenuItem value="FINALREPORT">Final Report</MenuItem>
                                                <MenuItem value="REJECTED">Rejected</MenuItem>
                                            </Select>
                                        </>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 3 }} />

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={loading.projects ? <CircularProgress size={20} color="inherit" /> : <Icon name="download1" size={18} />}
                                onClick={() => handleExport('projects', isTechnicalFlow ? 'admin_projects' : '')}
                                disabled={loading.projects || loading.client_requests}
                                sx={{
                                    backgroundColor: COLORS.PRIMARY,
                                    color: '#fff',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    minWidth: '280px',
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                    '&:hover': { backgroundColor: COLORS.SECONDARY, boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                                }}
                            >
                                {loading.projects ? "Generating CSV..." : `Export ${isTechnicalFlow ? 'Admin Projects' : 'Projects'} to CSV`}
                            </Button>
                            
                            {isTechnicalFlow && (
                                <Button
                                    variant="outlined"
                                    startIcon={loading.client_requests ? <CircularProgress size={20} color="inherit" /> : <Icon name="download1" size={18} />}
                                    onClick={() => handleExport('projects', 'client_requests')}
                                    disabled={loading.projects || loading.client_requests}
                                    sx={{
                                        borderColor: COLORS.PRIMARY,
                                        color: COLORS.PRIMARY,
                                        borderWidth: '2px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        minWidth: '280px',
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        '&:hover': { backgroundColor: `${COLORS.PRIMARY}10`, borderColor: COLORS.PRIMARY, borderWidth: '2px' }
                                    }}
                                >
                                    {loading.client_requests ? "Generating CSV..." : "Export Client Requests to CSV"}
                                </Button>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* User Export */}
                <Grid item xs={12} sx={{ width: '100%' }}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 3, 
                            borderRadius: '20px', 
                            border: `1px solid ${COLORS.BORDER || '#eee'}`,
                            background: '#fff',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Icon name="Users" size={24} color={COLORS.PRIMARY} />
                            <Typography sx={{ fontWeight: 800, color: COLORS.TEXT, fontSize: '20px' }}>
                                User & Engineer Data
                            </Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="From Date"
                                    type="date"
                                    fullWidth
                                    value={userFilters.fromDate}
                                    onChange={(e) => setUserFilters({ ...userFilters, fromDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={{ '& input': { textTransform: 'uppercase' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="To Date"
                                    type="date"
                                    fullWidth
                                    value={userFilters.toDate}
                                    onChange={(e) => setUserFilters({ ...userFilters, toDate: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                    sx={{ '& input': { textTransform: 'uppercase' } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={userFilters.role}
                                        label="Role"
                                        onChange={(e) => setUserFilters({ ...userFilters, role: e.target.value })}
                                    >
                                        <MenuItem value="all">All Roles</MenuItem>
                                        <MenuItem value="ENGINEER">Engineer</MenuItem>
                                        <MenuItem value="CLIENT">Client</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Account Status</InputLabel>
                                    <Select
                                        value={userFilters.status}
                                        label="Account Status"
                                        onChange={(e) => setUserFilters({ ...userFilters, status: e.target.value })}
                                    >
                                        <MenuItem value="all">All Statuses</MenuItem>
                                        <MenuItem value="APPROVED">Approved</MenuItem>
                                        <MenuItem value="PENDING">Pending</MenuItem>
                                        <MenuItem value="REJECTED">Rejected</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        
                        <Divider sx={{ my: 3 }} />

                        <Button
                            variant="contained"
                            startIcon={loading.users ? <CircularProgress size={20} color="inherit" /> : <Icon name="download1" size={18} />}
                            onClick={() => handleExport('users')}
                            disabled={loading.users}
                            sx={{
                                backgroundColor: COLORS.PRIMARY,
                                color: '#fff',
                                textTransform: 'none',
                                fontWeight: 700,
                                width: '280px',
                                px: 4,
                                py: 1,
                                borderRadius: '12px',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                '&:hover': { backgroundColor: COLORS.SECONDARY, boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                            }}
                        >
                            {loading.users ? "Generating CSV..." : "Export Users to CSV"}
                        </Button>
                    </Paper>
                </Grid>

                {/* Custom Checklist Questions & Import (Field Inspection Portal only) */}
                {isTechnicalFlow && (
                    <Grid item xs={12} sx={{ width: '100%' }}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                borderRadius: '20px', 
                                border: `1px solid ${COLORS.BORDER || '#eee'}`,
                                background: '#fff',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                                <Icon name="setting" size={24} color={COLORS.PRIMARY} />
                                <Typography sx={{ fontWeight: 800, color: COLORS.TEXT, fontSize: '20px' }}>
                                    Custom Checklist Questions & Predefined Check Import
                                </Typography>
                            </Box>

                            <Grid container spacing={4}>
                                {/* Export Custom Questions */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontWeight: 700, color: COLORS.TEXT, mb: 1.5, fontSize: '16px' }}>
                                        Export Custom Checklist Questions
                                    </Typography>
                                    <Typography sx={{ color: COLORS.LIGHT_SECONDARY, mb: 2, fontSize: '14px' }}>
                                        Download all engineer-entered custom checklist questions in CSV format.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                                        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                            <InputLabel>Category</InputLabel>
                                            <Select
                                                multiple
                                                value={selectedCategoryIds}
                                                label="Category"
                                                onChange={handleCategoryChange}
                                                MenuProps={customMenuProps}
                                                renderValue={(selected) => {
                                                    if (selected.includes('all')) return 'All Categories';
                                                    if (selected.length > 2) {
                                                        return `${selected.length} Categories Selected`;
                                                    }
                                                    return selected.map(id => {
                                                        const cat = issueCategories.find(c => c._id === id);
                                                        return cat ? cat.name : id;
                                                    }).join(', ');
                                                }}
                                                sx={{
                                                    '& .MuiSelect-select': {
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        paddingRight: '32px !important'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="all">
                                                    <Checkbox checked={selectedCategoryIds.includes('all')} size="small" sx={{ mr: 1, p: 0 }} />
                                                    <ListItemText primary="All Categories" primaryTypographyProps={{ style: { fontFamily: FONT_FAMILY, fontSize: '14px' } }} />
                                                </MenuItem>
                                                {issueCategories.map(cat => {
                                                    const isSelected = selectedCategoryIds.includes(cat._id);
                                                    return (
                                                        <MenuItem key={cat._id} value={cat._id}>
                                                            <Checkbox checked={isSelected} size="small" sx={{ mr: 1, p: 0 }} />
                                                            <ListItemText primary={cat.name} primaryTypographyProps={{ style: { fontFamily: FONT_FAMILY, fontSize: '14px' } }} />
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>

                                        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                            <InputLabel>Status</InputLabel>
                                            <Select
                                                value={selectedStatus}
                                                label="Status"
                                                onChange={(e) => setSelectedStatus(e.target.value)}
                                                MenuProps={customMenuProps}
                                            >
                                                <MenuItem value="PENDING">Pending</MenuItem>
                                                <MenuItem value="ADDED">Added</MenuItem>
                                                <MenuItem value="all">All Statuses</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        startIcon={loadingCustom ? <CircularProgress size={22} color="inherit" /> : <Icon name="download1" size={20} />}
                                        onClick={handleExportCustomChecks}
                                        disabled={loadingCustom}
                                        sx={{
                                            backgroundColor: COLORS.PRIMARY,
                                            color: '#fff',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            width: '100%',
                                            py: 1.6,
                                            borderRadius: '14px',
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                            '&:hover': { backgroundColor: COLORS.SECONDARY, boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                                        }}
                                    >
                                        {loadingCustom ? "Generating CSV..." : "Export Custom Questions to CSV"}
                                    </Button>
                                </Grid>

                                {/* Import Predefined Checklist Questions */}
                                <Grid item xs={12} md={6}>
                                    <Typography sx={{ fontWeight: 700, color: COLORS.TEXT, mb: 1.5, fontSize: '16px' }}>
                                        Import Predefined Checks
                                    </Typography>
                                    <Typography sx={{ color: COLORS.LIGHT_SECONDARY, mb: 2.5, fontSize: '14px' }}>
                                        Upload an Excel (.xlsx) or CSV (.csv) file to bulk-add predefined standard checks.
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', minHeight: { sm: 40 } }}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            sx={{
                                                borderColor: COLORS.PRIMARY,
                                                color: COLORS.PRIMARY,
                                                borderWidth: '1px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '14px',
                                                height: 40,
                                                flex: 1,
                                                width: '100%',
                                                borderRadius: '8px',
                                                '&:hover': { backgroundColor: `${COLORS.PRIMARY}10`, borderColor: COLORS.PRIMARY, borderWidth: '1px' }
                                            }}
                                        >
                                            Choose Excel or CSV File
                                            <input
                                                id="excel-file-input"
                                                type="file"
                                                accept=".xlsx,.csv"
                                                hidden
                                                onChange={(e) => setSelectedFile(e.target.files[0])}
                                            />
                                        </Button>
                                        <Box sx={{ 
                                            flex: 1, 
                                            height: 40, 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            border: `1px solid ${COLORS.BORDER || '#ccc'}`,
                                            borderRadius: '8px',
                                            px: 1.5,
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}>
                                            <Typography sx={{ 
                                                fontSize: '14px', 
                                                color: selectedFile ? COLORS.TEXT : COLORS.LIGHT_SECONDARY,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                mr: 1
                                            }}>
                                                {selectedFile ? selectedFile.name : "No file chosen"}
                                            </Typography>
                                            {selectedFile && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedFile(null);
                                                        const fileInput = document.getElementById('excel-file-input');
                                                        if (fileInput) fileInput.value = '';
                                                    }}
                                                    sx={{ color: '#F44336', p: 0.5 }}
                                                    title="Remove selected file"
                                                >
                                                    <Icon name="close2" size={16} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>

                                    <Button
                                        variant="contained"
                                        startIcon={loadingImport ? <CircularProgress size={22} color="inherit" /> : <Icon name="plus" size={20} />}
                                        onClick={handleImportExcel}
                                        disabled={loadingImport || !selectedFile}
                                        sx={{
                                            backgroundColor: COLORS.PRIMARY,
                                            color: '#fff',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            width: '100%',
                                            py: 1.6,
                                            borderRadius: '14px',
                                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                                            '&:hover': { backgroundColor: COLORS.SECONDARY, boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                                        }}
                                    >
                                        {loadingImport ? "Importing..." : "Import Predefined Checks"}
                                    </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            )}

            {/* Custom Success/Error Popup Dialog */}
            <Dialog 
                open={popupMsg.open} 
                onClose={() => setPopupMsg(prev => ({ ...prev, open: false }))}
                PaperProps={{
                    sx: {
                        borderRadius: '20px',
                        padding: 2,
                        minWidth: '320px',
                        maxWidth: '450px',
                        textAlign: 'center'
                    }
                }}
            >
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 3 }}>
                    <Box sx={{ 
                        width: 56, 
                        height: 56, 
                        borderRadius: '50%', 
                        backgroundColor: popupMsg.type === 'success' ? '#E8F5E9' : '#FFEBEE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Icon 
                            name={popupMsg.type === 'success' ? 'check2' : 'close2'} 
                            size={28} 
                            color={popupMsg.type === 'success' ? '#2E7D32' : '#C62828'} 
                        />
                    </Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 800, fontSize: '20px', color: COLORS.TEXT }}>
                        {popupMsg.title}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '14px', color: COLORS.LIGHT_SECONDARY }}>
                        {popupMsg.text}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button 
                        onClick={() => setPopupMsg(prev => ({ ...prev, open: false }))}
                        variant="contained"
                        sx={{
                            backgroundColor: popupMsg.type === 'success' ? COLORS.PRIMARY : '#C62828',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 4,
                            py: 1,
                            borderRadius: '10px',
                            '&:hover': {
                                backgroundColor: popupMsg.type === 'success' ? COLORS.SECONDARY : '#B71C1C'
                            }
                        }}
                    >
                        Okay
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    </Box>
    );
};

export default ExportData;